/**
 * WalkthroughTimeline — vertical timeline rendering of cognitive walkthrough
 * moments. Replaces the accordion presentation in the legacy bundle view.
 * All moments are visible by default; sorted by attention stage.
 */

interface WalkthroughMoment {
  attention_stage?: string;
  stage?: string;
  element_focus?: string;
  internal_monologue?: string;
  cognitive_load?: string;
}

const STAGE_ORDER = [
  'first_fixation',
  'initial_scan',
  'deep_processing',
  'decision',
  'recall',
];

const STAGE_COLORS: Record<
  string,
  { badge: string; nodeColor: string; ring: string }
> = {
  first_fixation: {
    badge: 'bg-purple-50 border-purple-200 text-purple-700',
    nodeColor: '#a855f7',
    ring: 'ring-purple-200',
  },
  initial_scan: {
    badge: 'bg-blue-50 border-blue-200 text-blue-700',
    nodeColor: '#3b82f6',
    ring: 'ring-blue-200',
  },
  deep_processing: {
    badge: 'bg-teal-50 border-teal-200 text-teal-700',
    nodeColor: '#14b8a6',
    ring: 'ring-teal-200',
  },
  decision: {
    badge: 'bg-amber-50 border-amber-200 text-amber-700',
    nodeColor: '#f59e0b',
    ring: 'ring-amber-200',
  },
  recall: {
    badge: 'bg-slate-50 border-slate-200 text-slate-600',
    nodeColor: '#94a3b8',
    ring: 'ring-slate-200',
  },
};

function stageOf(moment: WalkthroughMoment): string {
  return moment.attention_stage ?? moment.stage ?? 'recall';
}

export function WalkthroughTimeline({ moments }: { moments: WalkthroughMoment[] }) {
  if (!moments || moments.length === 0) return null;

  const sorted = [...moments].sort((a, b) => {
    const ai = STAGE_ORDER.indexOf(stageOf(a));
    const bi = STAGE_ORDER.indexOf(stageOf(b));
    return (ai === -1 ? STAGE_ORDER.length : ai) - (bi === -1 ? STAGE_ORDER.length : bi);
  });

  return (
    <div>
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-3">
        Cognitive Walkthrough
      </p>
      <div className="relative pl-4">
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200" />

        <div className="space-y-4">
          {sorted.map((moment, idx) => {
            const stage = stageOf(moment);
            const cfg = STAGE_COLORS[stage] ?? STAGE_COLORS.recall;
            return (
              <div key={idx} className="relative flex gap-3">
                <div
                  className={`absolute -left-4 mt-1 w-3.5 h-3.5 rounded-full border-2 border-white ring-1 ${cfg.ring} flex-shrink-0`}
                  style={{ backgroundColor: cfg.nodeColor }}
                />

                <div className="flex-1 min-w-0 bg-white rounded-xl border border-slate-100 p-3 shadow-sm space-y-1.5">
                  <span
                    className={`inline-block text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${cfg.badge}`}
                  >
                    {stage.replace(/_/g, ' ')}
                  </span>

                  {moment.element_focus && (
                    <p className="text-[11px] font-semibold text-navy-900">
                      {moment.element_focus}
                    </p>
                  )}

                  {moment.internal_monologue && (
                    <p className="text-xs text-slate-600 italic leading-relaxed">
                      &ldquo;{moment.internal_monologue}&rdquo;
                    </p>
                  )}

                  {moment.cognitive_load && (
                    <p className="text-[10px] font-mono text-slate-400">
                      cognitive load: {moment.cognitive_load}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
